import dayjs from "dayjs";
import {
  PlayCircleOutlined,
  UploadOutlined,
  ExclamationCircleOutlined,
  SaveOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import { Col, Row, Modal, Spin, Upload } from "antd";
import { useFormik } from "formik";
import { get, indexOf, isEmpty, toLower } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Button from "../../../components/functional_components/Button/PFButton";
import DetailBox from "../../../components/functional_components/DetailBox/DetailBox";
import ImportPlanning from "../../../services/api/ImportPlanning";
import { langMonth, fileFormatXextension } from "../../../utils/properties";
import { showError, showSuccess, trimData, exportFile } from "../../../utils/utilities";
import customParseFormat from "dayjs/plugin/customParseFormat";
const { Dragger } = Upload;
const { confirm } = Modal;
dayjs.extend(customParseFormat);

const MonthPlanning = () => {
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", {}));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const [state, setState] = useState({
    loading: false,
    uploadTimesheetLoading: false,
    copyPlanningm1Loading: false,
    uploadCostsModal: false,
    importCostsPlanModal: false,
    importInvoicePlanModal: false,
    fileList: [],
    data: [],
    dataMessages: [],
    freezedMonth: {
      freezed: false,
      currentMonth: null,
    },
    nextMonth: null,
    runningMonth: null,
  });

  useEffect(() => {
    getMonth();
  }, []);

  const getMonth = async () => {
    const month = await ImportPlanning.lastFreeze();
    if (!month.error) {
      setState((prevState) => ({
        ...prevState,
        freezedMonth: {
          freezed: month.data.closed,
          currentMonth: month.data.month.toString(),
          lastFreezedMonth: month.data.lastMonthClosed.toString(),
          runningMonth: month.data.runningMonth.toString(),
        },
        nextMonth: month.data.nextMonthToOpen,
      }));
    } else showError(get(month, "error.messageCode", "ER000"));
  };

  const notAllowedProjectDownload = async () => {
    const downloadType = fileFormatXextension["csv"];
    const file = await ImportPlanning.downloadProjectNotApproved();
    if (!file.error) {
      const blob = new Blob([file], { type: downloadType });
      exportFile(blob, `${t("monthPlanning.notApprovedProjects")}.csv`);
    } else showError(get(file, "error.messageCode", "ER000"));
  };

  const freezedMonth = async () => {
    confirm({
      title: `${t("monthPlanning.freezesMonthModal")} ${dayjs(state.freezedMonth.currentMonth, "YYYYMM").format(
        dateFormat
      )}?`,
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setState((prevState) => ({ ...prevState, freezesMonthLoading: true }));
        const ok = await ImportPlanning.closeMonth();
        if (!ok.error) {
          showSuccess();
        } else {
          showError(get(ok, "error.messageCode", "ER000"));
          if (ok.error.messageCode === "ER022") notAllowedProjectDownload();
        }
        setState((prevState) => ({ ...prevState, freezesMonthLoading: false }));
      },
      onCancel() {},
    });
  };

  // With Timesheet (Fase 3) this button is not needed
  // const uploadTimesheet = async () => {
  //   confirm({
  //     title: `${t("monthPlanning.uploadTimesheetModal")} ${dayjs(state.nextMonth, "YYYYMM").format(dateFormat)}?`,
  //     icon: <ExclamationCircleOutlined />,
  //     onOk: async () => {
  //       setState((prevState => ({ ...prevState, uploadTimesheetLoading: true })));
  //       const ok = await ImportPlanning.uploadTimesheet()
  //       if (!ok.error) {
  //         showSuccess()
  //       } else {
  //         showError(get(ok, "error.messageCode", "ER000"));
  //       }
  //       setState((prevState => ({ ...prevState, uploadTimesheetLoading: false })));
  //     },
  //     onCancel() { },
  //   });
  // }

  const copyPlanningm1 = async () => {
    confirm({
      title: `${t("monthPlanning.copyPlanningm-1Modal", {
        date: dayjs(state.freezedMonth.currentMonth, "YYYYMM").format(dateFormat),
      })}`,
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        setState((prevState) => ({ ...prevState, copyPlanningm1Loading: true }));
        const ok = await ImportPlanning.copyPlanningm1();
        if (!ok.error) {
          showSuccess();
        } else {
          showError(get(ok, "error.messageCode", "ER000"));
        }
        setState((prevState) => ({ ...prevState, copyPlanningm1Loading: false }));
      },
      onCancel() {},
    });
  };

  const formikDocument = useFormik({
    initialValues: { file: "" },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.file) errors.file = "Required!";
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async () => {
      const formData = new FormData();
      if (isEmpty(state.fileList)) {
        const fakeFile = new File([], "fakefile.txt");
        formData.append("file", fakeFile);
      } else {
        state.fileList.forEach((file) => {
          formData.append("file", file);
        });
      }
      setState((prevState) => ({ ...prevState, loading: true }));
      if (state.uploadCostsModal) {
        const response = await ImportPlanning.uploadCosts(formData);
        if (!response.error) {
          showSuccess();
          const blob = new Blob([response]);
          exportFile(blob, "upload-costs-logs.csv");
          formikDocument.resetForm();
        } else showError(get(response, "error.messageCode", "ER000"));
      }
      if (state.importCostsPlanModal) {
        const response = await ImportPlanning.uploadCostsPlan(formData);
        if (!response.error) {
          showSuccess();
          formikDocument.resetForm();
        } else showError(get(response, "error.messageCode", "ER000"));
      }
      if (state.importInvoicePlanModal) {
        const response = await ImportPlanning.uploadInvoicePlan(formData);
        if (!response.error) {
          showSuccess();
          formikDocument.resetForm();
        } else showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({
        ...prevState,
        uploadCostsModal: false,
        importCostsPlanModal: false,
        importInvoicePlanModal: false,
        loading: false,
        fileList: [],
      }));
    },
  });

  const uploadCosts = {
    multiple: false,
    onRemove: (file) => {
      const index = state.fileList.indexOf(file);
      const newFileList = state.fileList.slice();
      newFileList.splice(index, 1);
      setState((prevState) => ({ ...prevState, fileList: newFileList }));
      formikDocument.setFieldValue("file", "");
    },
    beforeUpload: (file) => {
      let ckFileList = [];
      let ckFileValue = "";
      if (indexOf(["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"], file.type) !== -1) {
        ((ckFileList = [file]), (ckFileValue = "ok"));
      } else {
        showError(t("projectEstimate.modal.errorFileFormat"));
      }
      setState((prevState) => ({ ...prevState, fileList: ckFileList }));
      formikDocument.setFieldValue("file", ckFileValue);
      return false;
    },
    fileList: state.fileList,
  };

  const uploadInvoice = {
    multiple: false,
    onRemove: (file) => {
      const index = state.fileList.indexOf(file);
      const newFileList = state.fileList.slice();
      newFileList.splice(index, 1);
      setState((prevState) => ({ ...prevState, fileList: newFileList }));
      formikDocument.setFieldValue("file", "");
    },
    beforeUpload: (file) => {
      let ckFileList = [];
      let ckFileValue = "";
      if (indexOf(fileFormatXextension["csv"], file.type) !== -1) {
        ((ckFileList = [file]), (ckFileValue = "ok"));
      } else {
        showError(t("projectEstimate.modal.errorFileFormat"));
      }
      setState((prevState) => ({ ...prevState, fileList: ckFileList }));
      formikDocument.setFieldValue("file", ckFileValue);
      return false;
    },
    fileList: state.fileList,
  };

  const editFreezedMonth = () => {
    confirm({
      title: t("monthPlanning.freezedMonthModal"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const editFreezed = await ImportPlanning.editFreezedMonth();
        if (!editFreezed.error) {
          showSuccess();
        } else {
          showError(t("errorsCode.errorFreezedMonth"));
        }
      },
      onCancel() {},
    });
  };

  const storeServiceBasePrice = () => {
    confirm({
      title: t("monthPlanning.storeServiceBasePriceModal"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const response = await ImportPlanning.storeServiceBasePrice();
        if (!response.error) {
          showSuccess();
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const recalculateCosts = () => {
    confirm({
      title: t("monthPlanning.recalculateCostsModal"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const response = await ImportPlanning.recalculateCosts();
        if (!response.error) {
          showSuccess();
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const generateETCTable = () => {
    confirm({
      title: t("monthPlanning.generateETCModal", {
        date: dayjs(state.freezedMonth.runningMonth, "YYYYMM").format(dateFormat),
      }),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const response = await ImportPlanning.generateETC();
        if (!response.error) {
          showSuccess();
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  return (
    <>
      <DetailBox title={t("monthPlanning.title")}>
        {state.freezedMonth.lastFreezedMonth && (
          <Row justify="space-around" className="mb-10">
            <b>{`${t("monthPlanning.lastFreeze")}${dayjs(state.freezedMonth.lastFreezedMonth, "YYYYMM").format(
              dateFormat
            )}`}</b>
          </Row>
        )}
        <Row>
          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.freezesMonth")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<PlayCircleOutlined className="icon-large" />}
                  clickCallback={freezedMonth}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>
          {/** With Timesheet (Fase 3) this button is not needed */}
          {/* <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.uploadTimesheet")}
              </Col>
              <Col span={4}>
                <Button currentIcon={<PlayCircleOutlined className="icon-large" />}
                  clickCallback={uploadTimesheet}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col> */}
          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.uploadCosts")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<UploadOutlined className="icon-large" />}
                  clickCallback={() => setState((prevState) => ({ ...prevState, uploadCostsModal: true }))}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.importInvoicePlan")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<UploadOutlined className="icon-large" />}
                  clickCallback={() => setState((prevState) => ({ ...prevState, importInvoicePlanModal: true }))}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row>
          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.importCostsPlan")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<UploadOutlined className="icon-large" />}
                  clickCallback={() => setState((prevState) => ({ ...prevState, importCostsPlanModal: true }))}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.copyPlanningm-1")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<PlayCircleOutlined className="icon-large" />}
                  clickCallback={copyPlanningm1}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>
          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.editFreezedMonth")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<PlayCircleOutlined className="icon-large" />}
                  clickCallback={() => editFreezedMonth()}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>
        </Row>

        <Row>
          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.storeServiceBasePrice")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<PlayCircleOutlined className="icon-large" />}
                  clickCallback={storeServiceBasePrice}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.recalculateCosts")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<PlayCircleOutlined className="icon-large" />}
                  clickCallback={recalculateCosts}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>

          <Col lg={8} md={12} xs={24} sm={12} type="flex" align="middle">
            <Row align="middle" justify="space-between" className="job-block mt-10">
              <Col span={20} className="fw-bold">
                {t("monthPlanning.generateETC")}
              </Col>
              <Col span={4}>
                <Button
                  currentIcon={<PlayCircleOutlined className="icon-large" />}
                  clickCallback={generateETCTable}
                  currentSize="large"
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </DetailBox>

      <Modal
        width={700}
        title={<b>{t("monthPlanning.uploadCosts")}</b>}
        open={state.uploadCostsModal}
        onCancel={() => setState((prevState) => ({ ...prevState, uploadCostsModal: false, fileList: [] }))}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikDocument.submitForm}
            isDisabled={!(formikDocument.dirty && formikDocument.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 48]}>
          <Col span={24}>
            <Row type="flex" justify="center" align="center" className="mb-10">
              <Col span={24}>
                <p>
                  {t("projectEstimate.modal.allowedExtensions")} {".xlsx"}
                </p>
              </Col>
              <Col span={24}>
                <Dragger {...uploadCosts}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">{t("projectEstimate.detail.tabs.preventiveForm.uploadTextEdit")}</p>
                </Dragger>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>

      <Modal
        width={700}
        title={<b>{t("monthPlanning.importCostsPlan")}</b>}
        open={state.importCostsPlanModal}
        onCancel={() => setState((prevState) => ({ ...prevState, importCostsPlanModal: false, fileList: [] }))}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikDocument.submitForm}
            isDisabled={!(formikDocument.dirty && formikDocument.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 48]}>
          <Col span={24}>
            <Row type="flex" justify="center" align="center" className="mb-10">
              <Col span={24}>
                <p>
                  {t("projectEstimate.modal.allowedExtensions")} {".csv"}
                </p>
              </Col>
              <Col span={24}>
                <Dragger {...uploadInvoice}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">{t("projectEstimate.detail.tabs.preventiveForm.uploadTextEdit")}</p>
                </Dragger>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>

      <Modal
        width={700}
        title={<b>{t("monthPlanning.importInvoicePlan")}</b>}
        open={state.importInvoicePlanModal}
        onCancel={() => setState((prevState) => ({ ...prevState, importInvoicePlanModal: false, fileList: [] }))}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikDocument.submitForm}
            isDisabled={!(formikDocument.dirty && formikDocument.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 48]}>
          <Col span={24}>
            <Row type="flex" justify="center" align="center" className="mb-10">
              <Col span={24}>
                <p>
                  {t("projectEstimate.modal.allowedExtensions")} {".csv"}
                </p>
              </Col>
              <Col span={24}>
                <Dragger {...uploadInvoice}>
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">{t("projectEstimate.detail.tabs.preventiveForm.uploadTextEdit")}</p>
                </Dragger>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default MonthPlanning;
